import {
    useCallback,
} from 'react'
import useHandleStatusRequest from 'app/hooks/useHandleStatusRequest'
import csvToJson from 'app/utils/csvToJson'

type Props = {
    onLoadData: (data: Record<string, any>[]) => void
    mandatoryFields: string[]
}

const useParseCsvFile = ({
    onLoadData,
    mandatoryFields,
}: Readonly<Props>) => {
    const {
        onSuccess: onSuccessHandler,
        onError: onErrorHandler,
    } = useHandleStatusRequest({
        id: 'parse-data',
        title: 'CSV file was successfully loaded',
    })

    const validateRecords = useCallback((data: Record<string, any>[]) => {
        if (mandatoryFields.length) {
            const missingFields = new Set<string>()
            const invalidRecords = data.filter((record) => {
                let hasError = false

                mandatoryFields.forEach((field) => {
                    if (!record[field]) {
                        missingFields.add(field)
                        hasError = true
                    }
                })

                return hasError
            })

            if (invalidRecords.length) {
                const pluralOrSingularForm = invalidRecords.length === 1 ? '1 record is' : `${invalidRecords.length} records are`

                onErrorHandler({
                    response: {
                        data: `${pluralOrSingularForm} missing some of mandatory fields: ${Array.from(missingFields).join(', ')}`,
                        status: 400,
                    },
                })
                return false
            }
        }
        return true
    }, [
        mandatoryFields,
        onErrorHandler,
    ])

    return useCallback(([file]: File[]) => {
        if (!file) {
            return
        }

        const reader = new FileReader()

        reader.onload = (event) => {
            const csvString = event.target?.result as string

            if (csvString) {
                const {
                    data,
                    error: parsingError,
                } = csvToJson(csvString)

                if (parsingError) {
                    onErrorHandler({
                        response: {
                            data: parsingError, status: 400,
                        },
                    })
                } else {
                    if (!validateRecords(data)) {
                        return
                    }
                    onSuccessHandler(null)
                    onLoadData(data)
                }
            }
        }
        reader.readAsText(file)
    }, [
        onErrorHandler,
        onLoadData,
        onSuccessHandler,
        validateRecords,
    ])
}

export default useParseCsvFile
